"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useHubs = void 0;
const react_1 = require("react");
const useLogger_1 = require("./useLogger");
const mutations_1 = require("../graphql/mutations");
const queries_1 = require("../graphql/queries");
const subscriptions_1 = require("../graphql/subscriptions");
const amplifyBootstrap_1 = require("../utils/amplifyBootstrap");
const useHubs = (params) => {
    const logger = (0, useLogger_1.useLogger)('useHubs');
    const [hubs, setHubs] = (0, react_1.useState)([]);
    const [hub, setHub] = (0, react_1.useState)(null);
    const [loading, setLoading] = (0, react_1.useState)(false);
    const [updating, setUpdating] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    const subscriptionsRef = (0, react_1.useRef)([]);
    const fetchedOnceRef = (0, react_1.useRef)(false);
    const subscribedHubIdsRef = (0, react_1.useRef)(null);
    const fetchAndSubscribeOnLoad = params?.fetchAndSubscribeOnLoad ?? false;
    const fetchHubs = (0, react_1.useCallback)(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: queries_1.listHubs,
                variables: { limit: 100 },
            });
            if (response && response.data) {
                let hubsList = [];
                if (Array.isArray(response.data.listHubs)) {
                    hubsList = response.data.listHubs;
                }
                else if (response.data.listHubs && Array.isArray(response.data.listHubs.items)) {
                    hubsList = response.data.listHubs.items;
                }
                else if (response.data.listHubs) {
                    hubsList = [response.data.listHubs];
                }
                setHubs(hubsList);
            }
            else {
                logger.warn('Unexpected API response structure:', response);
                setHubs([]);
            }
        }
        catch (error) {
            logger.error('Error fetching hubs:', { error });
            setError(error);
            setHubs([]);
        }
        finally {
            fetchedOnceRef.current = true;
            setLoading(false);
        }
    }, []);
    const getHubById = (0, react_1.useCallback)(async (id) => {
        try {
            setLoading(true);
            setError(null);
            const response = await amplifyBootstrap_1.API.graphql({
                query: queries_1.getHub,
                variables: { id },
            });
            const hubData = response.data.getHub;
            setHub(hubData);
            return hubData;
        }
        catch (error) {
            logger.error('Error fetching hub:', { error });
            setError(error);
            throw error;
        }
        finally {
            setLoading(false);
        }
    }, []);
    const addHub = (0, react_1.useCallback)(async (input) => {
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: mutations_1.createHub,
                variables: { input },
            });
            return response.data.createHub;
        }
        catch (error) {
            logger.error('Error creating hub:', { error });
            throw error;
        }
    }, []);
    const modifyHub = (0, react_1.useCallback)(async (input) => {
        try {
            setUpdating(true);
            const response = await amplifyBootstrap_1.API.graphql({
                query: mutations_1.updateHub,
                variables: { input },
            });
            return response.data.updateHub;
        }
        catch (error) {
            logger.error('Error updating hub:', { error });
            throw error;
        }
        finally {
            setUpdating(false);
        }
    }, []);
    const updateHubAddress = (0, react_1.useCallback)(async (input) => {
        try {
            setUpdating(true);
            const response = await amplifyBootstrap_1.API.graphql({
                query: mutations_1.updateHubAddress,
                variables: { input },
            });
            return response.data.updateHubAddress;
        }
        catch (error) {
            logger.error('Error updating hub address:', { error });
            throw error;
        }
        finally {
            setUpdating(false);
        }
    }, []);
    const removeHub = (0, react_1.useCallback)(async (id) => {
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: mutations_1.deleteHub,
                variables: { id },
            });
            return response.data.deleteHub;
        }
        catch (error) {
            logger.error('Error deleting hub:', { error });
            throw error;
        }
    }, []);
    const clear = (0, react_1.useCallback)(() => {
        setHubs([]);
    }, []);
    const unsubscribe = (0, react_1.useCallback)(() => {
        if (subscriptionsRef.current.length === 0) {
            return;
        }
        try {
            subscriptionsRef.current.forEach((subscription) => {
                if (subscription && typeof subscription.unsubscribe === 'function') {
                    subscription.unsubscribe();
                }
            });
        }
        catch (error) {
            logger.error('Error cleaning up subscriptions:', { error });
        }
    }, []);
    const subscribe = (0, react_1.useCallback)(() => {
        unsubscribe();
        try {
            // `onHubCreated` is a GLOBAL subscription — it fires for every hub
            // created on the account, not just this user's. Appending the payload
            // directly put other people's hubs (and every gateway provisioned
            // anywhere) straight into the local list, bypassing the relation filter
            // that `listHubs` applies server-side. Re-fetch instead, so the server
            // decides what this user is allowed to see. Hub creation is a rare
            // provisioning event, so the extra query costs nothing in practice.
            const createSubscription = amplifyBootstrap_1.API.graphql({
                query: subscriptions_1.onCreateHub,
            }).subscribe({
                next: ({ data }) => {
                    if (data?.onHubCreated) {
                        fetchHubs();
                    }
                },
                error: (error) => logger.error('Create subscription error:', error),
            });
            const updateSubscription = amplifyBootstrap_1.API.graphql({
                query: subscriptions_1.onUpdateHub,
            }).subscribe({
                next: ({ data }) => {
                    const updatedHub = data.onHubUpdated;
                    if (updatedHub) {
                        setHubs((prevHubs) => prevHubs.map((hub) => (hub.id === updatedHub.id ? updatedHub : hub)));
                    }
                },
                error: (error) => logger.error('Update subscription error:', error),
            });
            const deleteSubscription = amplifyBootstrap_1.API.graphql({
                query: subscriptions_1.onDeleteHub,
            }).subscribe({
                next: ({ data }) => {
                    const deletedHub = data.onHubDeleted;
                    if (deletedHub) {
                        setHubs((prevHubs) => prevHubs.filter((hub) => hub.id !== deletedHub.id));
                    }
                },
                error: (error) => logger.error('Delete subscription error:', error),
            });
            subscriptionsRef.current = [createSubscription, updateSubscription, deleteSubscription];
        }
        catch (error) {
            logger.error('Error setting up subscriptions:', { error });
        }
    }, [fetchHubs, unsubscribe]);
    const fetchAndSubscribe = (0, react_1.useCallback)(() => {
        fetchHubs();
        subscribe();
    }, [fetchHubs, subscribe]);
    (0, react_1.useEffect)(() => {
        unsubscribe();
        if (fetchAndSubscribeOnLoad) {
            fetchAndSubscribe();
        }
        return () => {
            unsubscribe();
        };
    }, []);
    // The server filters these subscriptions to the hubs the caller was linked to
    // when they opened (KAN-751), so a base station linked later (installer or
    // homeowner) needs them reopened to update live. The first list after load
    // is the baseline: the subscriptions opened at mount already cover it. Keyed
    // on `hubs` itself, not the id string: a first fetch of [] must still set the
    // baseline, or a homeowner's first base station would never reopen them.
    (0, react_1.useEffect)(() => {
        if (!fetchedOnceRef.current || subscriptionsRef.current.length === 0)
            return;
        const hubIdsKey = hubs
            .map((h) => h.id)
            .sort()
            .join(',');
        if (subscribedHubIdsRef.current !== null && subscribedHubIdsRef.current !== hubIdsKey) {
            subscribe();
        }
        subscribedHubIdsRef.current = hubIdsKey;
    }, [hubs, subscribe]);
    const getHubConfig = (0, react_1.useCallback)(async (id) => {
        try {
            const hub = await getHubById(id);
            if (hub && hub.config) {
                return JSON.parse(hub.config);
            }
            else {
                return {};
            }
        }
        catch (error) {
            logger.error('Error fetching hub config:', { error });
            throw error;
        }
    }, [getHubById]);
    const updateHubConfigData = (0, react_1.useCallback)(async (id, configData) => {
        try {
            const configStr = JSON.stringify(configData);
            const response = await amplifyBootstrap_1.API.graphql({
                query: mutations_1.updateHubConfig,
                variables: { id, config: configStr },
            });
            return response.data.updateHubConfig;
        }
        catch (error) {
            logger.error('Error updating hub config:', { error });
            throw error;
        }
    }, []);
    /**
     * Sets the hub's electricity pricing (Electrical.PriceModel / PriceModelValues).
     * The only hub-config write the backend accepts from an app: updateHubConfig
     * has no resolver on purpose, so writes through it are silently dropped.
     */
    const updateHubEnergyRate = (0, react_1.useCallback)(async (hubId, priceModel, priceModelValues) => {
        const response = await amplifyBootstrap_1.API.graphql({
            query: mutations_1.updateHubEnergyRate,
            variables: {
                input: { hubId, priceModel, priceModelValues: JSON.stringify(priceModelValues) },
            },
        });
        // Amplify resolves (not rejects) when the resolver returns errors[].
        if (response?.errors?.length) {
            logger.error('Error updating hub energy rate:', { errors: response.errors });
            throw new Error(response.errors[0]?.message || 'Failed: updateHubEnergyRate');
        }
        return response.data?.updateHubEnergyRate;
    }, []);
    return {
        hubs,
        hub,
        loading,
        updating,
        error,
        clear,
        fetchHubs,
        getHubById,
        addHub,
        modifyHub,
        updateHubAddress,
        removeHub,
        getHubConfig,
        updateHubConfigData,
        updateHubEnergyRate,
    };
};
exports.useHubs = useHubs;
